import { Dispatch } from 'redux'
import { getApiErrorMessage, getCSRFToken, hasApiErrorCode } from '../../lib/utils'
import {
	ADDPURCHASESUPLOAD_FAILURE,
	ADDPURCHASESUPLOAD_RESETFILE,
	ADDPURCHASESUPLOAD_SENDING,
	ADDPURCHASESUPLOAD_SENT,
	ADDPURCHASESUPLOAD_SETFILE,
	ADDPURCHASESUPLOAD_UNCERTAIN,
} from '../slices/addPurchasesUploadSlice'
import { UPLOAD_FILE_DATA_ADDITION } from '../slices/csvFilesSlice'
import { NeoniApi, RequestOptions } from '../../lib/api'

type IAddPurchasesUploadFile = {
	file: string
	accountingPeriodId: string
	filename: string
}

// Stores the file blob name and associated accounting period in the store, ready for upload use
export const setAddPurchaseUploadFile =
	({ file, accountingPeriodId, filename }: IAddPurchasesUploadFile) =>
	async (dispatch: Dispatch) => {
		dispatch({
			type: ADDPURCHASESUPLOAD_SETFILE,
			payload: {
				file,
				accountingPeriodId,
				filename,
			},
		})
	}

export const addPurchasesUploadFile =
	(api: NeoniApi, { file, accountingPeriodId, filename }: IAddPurchasesUploadFile, options?: RequestOptions) =>
	async (dispatch: Dispatch) => {
		dispatch({
			type: ADDPURCHASESUPLOAD_SENDING,
		})

		let submitted = false
		try {
			const csrf = await getCSRFToken(options?.signal)
			if (options?.signal?.aborted) return

			// Convert blob to a file again
			const fileResponse = await fetch(file, { signal: options?.signal })
			const unblobbedFile = await fileResponse.blob()
			if (options?.signal?.aborted) return

			// Use form data since we're sending a file too
			const formData = new FormData()
			formData.append('file', unblobbedFile)
			formData.append('accountingPeriodId', accountingPeriodId)
			formData.append('filename', filename)
			formData.append('_csrf', csrf)

			submitted = true
			const body = options
				? await api.form('csv/purchases', formData, options)
				: await api.form('csv/purchases', formData)
			if (options?.signal?.aborted) return

			if (body.success === true) {
				if (body.accountingPeriodId !== accountingPeriodId) {
					dispatch({
						type: ADDPURCHASESUPLOAD_UNCERTAIN,
						payload: 'The upload result referenced a different accounting period.',
					})
					return
				}
				// Successfully uploaded the file, show any errors or success
				dispatch({
					type: ADDPURCHASESUPLOAD_SENT,
					payload: {
						accountingPeriodId: body.accountingPeriodId,
						attempts: body.attemptCount,
						errors: body.errors,
						numberOfRecords: body.numberOfRecords,
					},
				})
				if (body.csvFileId !== undefined) {
					dispatch({
						type: UPLOAD_FILE_DATA_ADDITION,
						payload: {
							accountingPeriodId: body.accountingPeriodId,
							id: body.csvFileId,
							filename: body.filename,
							rowCount: body.numberOfRecords,
							uploaded_date: body.uploaded_date,
							uploader: body.uploader,
						},
					})
				}

				// Release the original blob from browser memory
				URL.revokeObjectURL(file)

				// Remove the blob file reference from the redux store
				dispatch({
					type: ADDPURCHASESUPLOAD_RESETFILE,
				})
				return {
					accountingPeriodId,
					valid: !body.errors?.length,
				}
			} else {
				// Error
				dispatch({
					type: ADDPURCHASESUPLOAD_UNCERTAIN,
					payload: getApiErrorMessage({ body }, 'Failed to upload this file.'),
				})
			}
		} catch (error) {
			if (options?.signal?.aborted) return
			// Security review follow-up to #857 item 6 (#882, blocker 2): item 6 made
			// READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE reject (lib/api.ts) with a
			// SerializableApiError instead of hanging forever, so this catch now actually runs for
			// those two codes - previously it only ever saw a generic network Error. Dispatching
			// `{ message: error.message }` here (an object) crashed AddPurchasesUpload.tsx, which
			// renders this payload directly as a React child, and the slice types it
			// PayloadAction<string>. getApiErrorMessage normalises any of these shapes to a plain
			// string (falling back to a generic message for the two notify-only codes above,
			// whose bodies carry only `{ code }` - the specific reason is already shown via the
			// existing global notify-only toast dispatched in lib/api.ts before this rejects).
			const denied = hasApiErrorCode(error, 'READ_ONLY_ACCESS') || hasApiErrorCode(error, 'ACCESS_AUDIT_UNAVAILABLE')
			dispatch({
				type: !submitted || denied ? ADDPURCHASESUPLOAD_FAILURE : ADDPURCHASESUPLOAD_UNCERTAIN,
				payload: getApiErrorMessage(error, !submitted || denied
					? 'Failed to upload this file. Please try again.'
					: 'Failed to upload this file.'),
			})
		}
	}
